/**
 * The icons offered for a desk marker and for a piece of furniture.
 *
 * A shortlist, not a limit: the picker searches the whole Material Design set, and this is
 * only what it opens on. It exists because opening on the first sixty of seven thousand icons
 * alphabetically is the same as offering nothing.
 *
 * Every name here resolves in **both** products. The portal draws from `@mdi/js` 7.4 and the
 * mobile app from `material_design_icons_flutter` 7.0.7296, and the app's set is the smaller
 * of the two — an icon that exists only in the newer one is tagged here, renders in the
 * editor, and silently draws nothing on the phone. `desk-icons.test.ts` checks the list
 * against both.
 */

/** Desks, seating and the rest of the furniture a floor is actually laid out with. */
const FURNITURE = [
  'desk', 'table-furniture', 'table-chair', 'chair-rolling', 'chair-school', 'seat',
  'sofa', 'sofa-single', 'sofa-outline', 'bed', 'bookshelf', 'file-cabinet', 'locker',
  'locker-multiple', 'archive', 'safe', 'bulletin-board', 'floor-lamp', 'desk-lamp', 'lamp',
];

/** What sits on the desk, and what a room is equipped with. */
const EQUIPMENT = [
  'monitor', 'monitor-multiple', 'laptop', 'desktop-tower-monitor', 'keyboard', 'tablet',
  'printer', 'scanner', 'deskphone', 'phone-classic', 'phone-in-talk', 'cellphone', 'headset',
  'microphone', 'webcam', 'video', 'projector', 'projector-screen', 'presentation',
  'television', 'server-network', 'router-wireless', 'wifi', 'power-plug',
];

/** Kitchen, break-out and the amenities people look for on a plan. */
const AMENITIES = [
  'coffee', 'coffee-maker', 'kettle', 'microwave', 'fridge-outline', 'water',
  'silverware-fork-knife', 'sleep', 'dumbbell', 'yoga', 'guitar-acoustic', 'gamepad-variant',
  'flower', 'tree', 'shower', 'toilet', 'baby-carriage', 'dog-service',
];

/** Getting around the building, and the things that have to be findable in one. */
const WAYFINDING = [
  'account-group', 'account-tie', 'human-male-board', 'wheelchair-accessibility', 'elevator',
  'stairs', 'door', 'door-closed', 'window-closed-variant', 'map-marker', 'information-outline',
  'bell', 'clock-outline', 'calendar-clock', 'clipboard-text', 'lock', 'key', 'package-variant',
  'trash-can', 'recycle', 'fire-extinguisher', 'medical-bag', 'air-conditioner', 'fan',
  'bike', 'car', 'ev-station',
];

/**
 * What the desk and furniture pickers open on.
 *
 * Grouped above so the order on screen goes from what a desk *is* to what is near it, rather
 * than alphabetically — which puts `air-conditioner` first and `desk` in the middle.
 */
const DESK_ICON_SHORTLIST: readonly string[] = [
  ...FURNITURE,
  ...EQUIPMENT,
  ...AMENITIES,
  ...WAYFINDING,
];

export default DESK_ICON_SHORTLIST;
